/**
 * `debug-run` skill template.
 *
 * Unlike the other entries in SKILL_TEMPLATES (which inline their content as
 * JS strings), the debug-run skill is rendered from the Handlebars partials in
 * `@chatsdet/diagnostic-playbook`. Source of truth = the playbook partials.
 *
 * Versioning: each released version is a frozen snapshot file under
 * `packages/canary-cli/skills/debug-run/history/<version>.md`. The current
 * version is stamped in `version.json` next to the history dir. To mint a new
 * version after editing partials, run:
 *
 *     bun run --filter=@canaryai/cli bump-skill debug-run [patch|minor|major]
 *
 * The drift test in `apps/api/tests/unit/diagnostics/skill-rendered.test.ts`
 * fails if the live `renderSkill()` output diverges from
 * `history/<currentVersion>.md`, forcing a bump rather than silent edits to a
 * previously-released version. `previousVersions` is auto-populated from the
 * history dir so `canary skill diff debug-run` can replay any released version.
 */

import { readFileSync, readdirSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import type { SkillTemplate } from './issue-log-xref.js';

const HERE = dirname(fileURLToPath(import.meta.url));

/**
 * Resolve the on-disk location of the debug-run skill assets.
 *
 * At runtime the assets live in one of two places depending on whether we
 * are running from the source tree (tests, dev), the bundled dist (npm
 * install), or from a published @canaryai/cli inside node_modules:
 *
 *   - source: packages/canary-cli/src/skill-templates/debug-run.ts
 *             → ../../skills/debug-run/
 *   - dist:   packages/canary-cli/dist/<chunk>.js
 *             → ./skills/debug-run/  (copied by `cp -r skills dist/`)
 *
 * The first candidate that has a readable `version.json` wins.
 */
function resolveSkillDir(): string {
  const candidates = [
    join(HERE, '..', '..', 'skills', 'debug-run'), // source tree
    join(HERE, 'skills', 'debug-run'),             // dist
    join(HERE, '..', 'skills', 'debug-run'),       // dist split-chunk variant
  ];
  for (const c of candidates) {
    try {
      readFileSync(join(c, 'version.json'), 'utf-8');
      return c;
    } catch {
      // try next
    }
  }
  throw new Error(
    `Could not locate debug-run skill assets relative to ${HERE}. Tried: ${candidates.join(', ')}`,
  );
}

const SKILL_DIR = resolveSkillDir();
const HISTORY_DIR = join(SKILL_DIR, 'history');

const versionMeta = JSON.parse(
  readFileSync(join(SKILL_DIR, 'version.json'), 'utf-8'),
) as { current: string };

function readVersion(version: string): string {
  return readFileSync(join(HISTORY_DIR, `${version}.md`), 'utf-8');
}

function listVersionsOnDisk(): string[] {
  return readdirSync(HISTORY_DIR)
    .filter((f) => f.endsWith('.md'))
    .map((f) => f.slice(0, -3));
}

const previousVersions: Record<string, () => string> = {};
for (const v of listVersionsOnDisk()) {
  if (v !== versionMeta.current) {
    previousVersions[v] = () => readVersion(v);
  }
}

export const debugRunTemplate: SkillTemplate = {
  name: 'debug-run',
  description:
    'Diagnose why a workflow test failed — root-cause an end-to-end run from its materialised artifacts',
  version: versionMeta.current,
  render: () => readVersion(versionMeta.current),
  previousVersions,
};
